import { useMemo, useState } from "react"
import { Bound } from "lib/@components/binding/Bound"
import { useHasAmendsAccess } from "authoring/routes/project/amends/lib/has-amends-access"
import {
    buildAudienceAmendsPayload,
    hasAudienceAmendsContent,
    normalizeAudienceAmends,
} from "../lib/audience-aware-amends.mjs"

export function AmendsBoundary({ children, amends = [], onSaveAmend, onDeleteAmend, options }) {
    if (!Array.isArray(amends))
        throw new Error(
            `AmendsBoundary requires amends to be an array of amends records, passed ${amends} of type ${typeof amends}`
        )
    if (typeof onSaveAmend !== "function") throw new Error("onSaveAmend must be a function")
    if (typeof onDeleteAmend !== "function") throw new Error("onDeleteAmend must be a function")

    const [localAmendsByAmendsKey, setLocalAmendsByAmendsKey] = useState({})
    const hasAmendsAccess = useHasAmendsAccess()

    const amendsByKey = useMemo(() => mapAmendsByAmendsKey(amends), [amends])
    const context = {
        getAmendsState,
        onOpenAmends,
        onChangeAmends,
        onCancelAmends,
        onSaveAmends,
        onEditAmends,
        onDeleteAmends,
    }

    return <Bound amendsContext={context}>{children}</Bound>

    function getAmendsState(amendsKey) {
        const localState = localAmendsByAmendsKey[amendsKey] ?? {}
        const savedAmends = localState.deleted ? null : amendsByKey[amendsKey]
        const savedNoteData = normalizeAudienceAmends(savedAmends?.payload ?? {})

        const savedContent = savedNoteData.content
        const hasSavedAmends = hasAudienceAmendsContent(savedNoteData)
        const editNoteData = localState.editNoteData ?? savedNoteData
        const editContent = editNoteData.content ?? savedContent

        const open = localState.open ?? hasSavedAmends
        const editMode = localState.editMode ?? !hasSavedAmends
        const mode = open ? (editMode ? "edit" : "view") : "closed"

        const userId = localState.userId ?? savedAmends?.userId ?? null
        const userName = localState.userName ?? savedAmends?.userName ?? null
        const isInherited = localState.isInherited ?? savedAmends?._isInherited ?? false
        const canEdit = options?.editMode !== false && (isInherited || !savedAmends || hasAmendsAccess({ userId }))
        const amendsStateForKey = {
            amendsKey,
            localState,
            savedAmends,
            savedNoteData,
            savedContent,
            content: editContent,
            hasSavedAmends,
            canEdit,
            isInherited,
            noteData: editNoteData,
        }
        const canOpenByPredicate = Boolean(options?.canOpen?.(amendsStateForKey) ?? true)

        return {
            id: localState.id ?? savedAmends?.id ?? null,
            userId,
            userName,
            canEdit,
            canOpen: canEdit && canOpenByPredicate,
            mode,
            isOpen: open,
            isEditMode: editMode,
            hasSavedAmends,
            isInherited,
            savedNoteData,
            savedContent,
            content: editMode ? editContent : editContent || savedContent,
            noteData: editNoteData,
            authorName: userName,
            savedAt: localState.savedAt ?? savedAmends?.modified ?? savedAmends?.date,
        }
    }

    function onOpenAmends(amendsKey) {
        const state = getAmendsState(amendsKey)
        if (!state.canOpen) return false

        const localAmendsState = localAmendsByAmendsKey[amendsKey] ?? {}
        const isDeleted = Boolean(localAmendsState.deleted)
        setAmendsState(amendsKey, {
            id: state.id,
            userId: state.userId,
            userName: state.userName,
            editContent: state.savedContent,
            editNoteData: state.savedNoteData,
            open: true,
            editMode: !state.hasSavedAmends,
            deleted: isDeleted,
        })
        return true
    }

    function onChangeAmends(amendsKey, value) {
        const state = getAmendsState(amendsKey)
        const noteData = typeof value === "object" && value !== null ? buildAudienceAmendsPayload(value) : null
        if (state.canEdit) {
            setAmendsState(amendsKey, {
                id: state.id,
                userId: state.userId,
                userName: state.userName,
                editContent: noteData?.content ?? value,
                editNoteData: noteData ?? buildAudienceAmendsPayload({ content: value }),
                open: true,
                editMode: true,
                deleted: false,
            })
        }
    }

    function onCancelAmends(amendsKey) {
        const state = getAmendsState(amendsKey)
        if (state.canEdit) {
            const localAmendsState = localAmendsByAmendsKey[amendsKey] ?? {}
            const isDeleted = Boolean(localAmendsState.deleted)
            setAmendsState(amendsKey, {
                id: state.id,
                userId: state.userId,
                userName: state.userName,
                editContent: state.savedContent,
                editNoteData: state.savedNoteData,
                open: state.hasSavedAmends,
                editMode: false,
                deleted: isDeleted,
            })
        }
    }

    async function onSaveAmends(amendsKey, content, { silent = false } = {}) {
        const state = getAmendsState(amendsKey)
        if (!state.canEdit) return null
        const nextNoteData =
            typeof content === "object" && content !== null ? buildAudienceAmendsPayload(content) : null
        const resolvedNoteData = nextNoteData ?? state.noteData
        const nextContent = resolvedNoteData.content ?? state.content

        let saved
        try {
            saved = await onSaveAmend({
                ...state,
                amendsKey,
                content: nextContent,
                internalContent: resolvedNoteData.internalContent,
                externalContent: resolvedNoteData.externalContent,
                noteMode: resolvedNoteData.noteMode,
                silent,
            })
        } catch (error) {
            console.error(`Failed to save amends for ${amendsKey}`, error)
            return null
        }
        if (!saved) return null

        setAmendsState(amendsKey, {
            id: saved?.id ?? state.id,
            userId: saved?.userId ?? state.userId ?? null,
            userName: saved?.userName ?? state.userName ?? null,
            editContent: nextContent,
            editNoteData: normalizeAudienceAmends(saved?.payload ?? resolvedNoteData),
            open: true,
            editMode: false,
            deleted: false,
            isInherited: false,
            savedAt: saved?.modified ?? saved?.date,
        })
        return saved
    }

    function onEditAmends(amendsKey) {
        const state = getAmendsState(amendsKey)
        if (state.canEdit) {
            setAmendsState(amendsKey, {
                id: state.id,
                userId: state.userId,
                userName: state.userName,
                editContent: state.content,
                editNoteData: state.noteData,
                open: true,
                editMode: true,
                deleted: false,
            })
        }
    }

    async function onDeleteAmends(amendsKey) {
        const state = getAmendsState(amendsKey)
        if (!state.canEdit) return null
        if (!state.hasSavedAmends) return null
        // Inherited notes have no current-cycle record to delete. Prior-cycle history
        // must not be altered — the author must save first to create a current-cycle record.
        if (state.isInherited) return null

        try {
            await onDeleteAmend({ ...state, amendsKey })
        } catch (error) {
            console.error(`Failed to delete amends for ${amendsKey}`, error)
            return null
        }

        setAmendsState(amendsKey, {
            id: null,
            userId: null,
            userName: null,
            editContent: "",
            editNoteData: buildAudienceAmendsPayload({ content: "" }),
            open: false,
            editMode: false,
            deleted: true,
            savedAt: null,
        })
        return true
    }

    function setAmendsState(amendsKey, nextState) {
        setLocalAmendsByAmendsKey((current) => ({
            ...current,
            [amendsKey]: {
                ...(current[amendsKey] ?? {}),
                ...nextState,
            },
        }))
    }
}

function mapAmendsByAmendsKey(amends) {
    return amends.reduce((result, amend) => {
        const amendsKey = amend?.payload?.amendsKey

        if (!amendsKey) {
            console.warn(`AmendsBoundary requires amend.payload.amendsKey (amend.id: ${amend?.id ?? "unknown"})`)
            return result
        }
        if (result[amendsKey]) {
            throw new Error(`AmendsBoundary received duplicate amendsKey: ${amendsKey}`)
        }

        result[amendsKey] = amend
        return result
    }, {})
}
